import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { ImagePlus, Trash2 } from 'lucide-react';
import styles from './ImagePicker.module.scss';

/* ────────────────────────────────────────────────────────────
 * ImagePicker — labelled image upload with live preview.
 *
 * Replaces the hand-rolled <input type="file"> + side <img> preview
 * pattern that recurred across Team Photo (CreateTeam), Store Logo
 * (AddNewStore), Item Photo (AddItems), etc.
 *
 *   <ImagePicker
 *     id="team-photo"
 *     label="Upload Team Photo"
 *     value={photoFile}                 // File | string URL | null
 *     onChange={(file) => setPhoto(file)}
 *     onError={(msg) => setError(msg)}
 *     error={errorFromOutside}
 *     placeholderSrc={defaultThumbnail}
 *     accept="image/png, image/jpeg"
 *     acceptedExtensions={['png', 'jpg', 'jpeg']}
 *     maxSizeBytes={4 * 1024 * 1024}
 *     helperText="File size limit: 4 MB"
 *   />
 *
 * Internal validation (size + extension) fires onError without
 * forwarding the file. External validation (server / required)
 * is shown via the `error` prop.
 * ──────────────────────────────────────────────────────────── */

const formatExtensions = (extensions) =>
  extensions.map((ext) => `.${ext.toLowerCase().replace(/^\./, '')}`);

const ImagePicker = ({
  id,
  label,
  value,
  onChange,
  onError,
  error,
  placeholderSrc,
  accept,
  acceptedExtensions,
  maxSizeBytes,
  helperText,
  required,
  disabled,
  className,
}) => {
  const inputRef = useRef(null);

  const objectUrl = useMemo(() => {
    if (value instanceof File) return URL.createObjectURL(value);
    return null;
  }, [value]);

  useEffect(() => {
    if (!objectUrl) return undefined;
    return () => URL.revokeObjectURL(objectUrl);
  }, [objectUrl]);

  const previewSrc = useMemo(() => {
    if (objectUrl) return objectUrl;
    if (typeof value === 'string' && value.length > 0) return value;
    return placeholderSrc;
  }, [objectUrl, value, placeholderSrc]);

  const hasImage = Boolean(objectUrl) || (typeof value === 'string' && value.length > 0);

  const handleFile = useCallback(
    (event) => {
      const file = event.target.files && event.target.files[0];
      if (!file) return;

      if (maxSizeBytes && file.size > maxSizeBytes) {
        const limitMb = Math.round(maxSizeBytes / (1024 * 1024));
        onError && onError(`File size should not exceed ${limitMb}MB.`);
        event.target.value = '';
        return;
      }

      if (acceptedExtensions && acceptedExtensions.length > 0) {
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        const allowed = acceptedExtensions.map((e) => e.toLowerCase().replace(/^\./, ''));
        if (!allowed.includes(ext)) {
          const list = formatExtensions(acceptedExtensions).join(', ');
          onError && onError(`Only ${list} files are allowed.`);
          event.target.value = '';
          return;
        }
      }

      onChange(file);
      event.target.value = '';
    },
    [maxSizeBytes, acceptedExtensions, onChange, onError]
  );

  const triggerPick = useCallback(() => {
    if (disabled) return;
    inputRef.current && inputRef.current.click();
  }, [disabled]);

  const clearImage = useCallback(() => {
    if (disabled) return;
    onChange(null);
  }, [disabled, onChange]);

  const wrapperClasses = [styles.wrapper, className || ''].filter(Boolean).join(' ');
  const frameClasses = [
    styles.frame,
    hasImage ? styles.frameWithImage : styles.frameEmpty,
    error ? styles.frameError : '',
    disabled ? styles.frameDisabled : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapperClasses}>
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label}
          {required && (
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}

      <div className={styles.body}>
        <button
          type="button"
          className={frameClasses}
          onClick={triggerPick}
          aria-label={hasImage ? 'Change image' : 'Choose image'}
          disabled={disabled}
        >
          {hasImage ? (
            <img className={styles.preview} src={previewSrc} alt="" />
          ) : (
            <span className={styles.placeholder}>
              {placeholderSrc ? (
                <img className={styles.preview} src={placeholderSrc} alt="" />
              ) : (
                <ImagePlus
                  className={styles.placeholderIcon}
                  size={36}
                  strokeWidth={1.5}
                />
              )}
            </span>
          )}
          <span className={styles.overlay} aria-hidden="true">
            <ImagePlus size={20} strokeWidth={1.75} />
            <span className={styles.overlayLabel}>
              {hasImage ? 'Change' : 'Upload'}
            </span>
          </span>
        </button>

        {hasImage && !disabled && (
          <button
            type="button"
            className={styles.remove}
            onClick={clearImage}
            aria-label="Remove image"
          >
            <Trash2 size={16} strokeWidth={1.75} />
            <span>Remove</span>
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        onChange={handleFile}
        disabled={disabled}
        className={styles.input}
      />

      {error ? (
        <span className={styles.error}>{error}</span>
      ) : helperText ? (
        <span className={styles.helperText}>{helperText}</span>
      ) : null}
    </div>
  );
};

ImagePicker.propTypes = {
  id: PropTypes.string,
  label: PropTypes.node,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(typeof File !== 'undefined' ? File : Object),
  ]),
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func,
  error: PropTypes.string,
  placeholderSrc: PropTypes.string,
  accept: PropTypes.string,
  acceptedExtensions: PropTypes.arrayOf(PropTypes.string),
  maxSizeBytes: PropTypes.number,
  helperText: PropTypes.node,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

ImagePicker.defaultProps = {
  id: undefined,
  label: null,
  value: null,
  onError: undefined,
  error: '',
  placeholderSrc: '',
  accept: 'image/png, image/jpeg',
  acceptedExtensions: ['png', 'jpg', 'jpeg'],
  maxSizeBytes: 4 * 1024 * 1024,
  helperText: '',
  required: false,
  disabled: false,
  className: '',
};

export default ImagePicker;
